import React, { memo } from 'react';
import {
  CartesianGrid,
  Dot as RechartsDot,
  DotProps,
  Label,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart as RechartsScatterChart,
  XAxis,
  YAxis,
} from 'recharts';
import { AxisDomain } from 'recharts/types/util/types';

const ScatterDot: React.FC<DotProps> = ({ cx, cy }) => {
  return (
    <RechartsDot
      cx={cx}
      cy={cy}
      fill="var(--main-2-color)"
      fillOpacity="0.6"
      r={3}
      strokeWidth="0"
    />
  );
};

export interface ScatterChartProps {
  xAxisLabel: string;
  xAxisDomain: AxisDomain;
  xAxisTickCount: number;
  yAxisLabel: string;
  yAxisDomain: AxisDomain;
  yAxisTickCount: number;
  data: { x: number; y: number }[];
  areas: { x1: number; x2: number; y1: number; y2: number; type: 'success' | 'danger' }[];
  lines?: { x: number; y: number };
  redDot?: { x: number; y: number };
  columnLabels?: {
    x1: number;
    x2: number;
    y1: number;
    y2: number;
    labels: { position: 'top' | 'topSubtitle' | 'bottom'; text: string }[];
  }[];
}

const Component: React.FC<ScatterChartProps> = ({
  xAxisLabel,
  xAxisDomain,
  xAxisTickCount,
  yAxisLabel,
  yAxisDomain,
  yAxisTickCount,
  data,
  areas,
  lines,
  redDot,
  columnLabels = [],
}) => {
  return (
    <ResponsiveContainer height={215} width="100%">
      <RechartsScatterChart data={data}>
        <CartesianGrid stroke="var(--gray-1-color)" strokeWidth="1px" />
        <XAxis
          allowDataOverflow
          axisLine={{ stroke: 'var(--gray-10-color)', strokeWidth: '6px' }}
          dataKey="x"
          domain={xAxisDomain}
          height={50}
          label={{
            value: xAxisLabel,
            fontSize: '8px',
            fontWeight: '300',
            position: 'insideBottom',
            fill: 'var(--text-3-color)',
            fillOpacity: '0.5',
          }}
          tick={{ fontSize: '10px', fontWeight: '300', fill: 'var(--gray-2-color)' }}
          tickCount={xAxisTickCount}
          tickMargin={14}
          tickSize={0}
          type="number"
        />
        <YAxis
          allowDataOverflow
          axisLine={{ stroke: 'var(--gray-1-color)', strokeWidth: '1px' }}
          dataKey="y"
          domain={yAxisDomain}
          label={{
            value: yAxisLabel,
            fontSize: '8px',
            fontWeight: '300',
            position: 'insideLeft',
            fill: 'var(--text-3-color)',
            fillOpacity: '0.5',
          }}
          tick={{ fontSize: '10px', fontWeight: '300', fill: 'var(--gray-2-color)' }}
          tickCount={yAxisTickCount}
          tickMargin={14}
          tickSize={0}
          type="number"
          width={40}
        />
        <Scatter shape={<ScatterDot />} />
        {lines?.x && <ReferenceLine stroke="var(--error-color)" strokeWidth="1px" x={lines.x} />}
        {lines?.y && <ReferenceLine stroke="var(--error-color)" strokeWidth="1px" y={lines.y} />}
        {redDot && <ReferenceDot fill="var(--error-color)" r={8} strokeWidth="0" {...redDot} />}
        {areas.map(({ type, ...coordinates }, index) => (
          <ReferenceArea
            fill={type === 'success' ? 'var(--success-color)' : 'var(--danger-color)'}
            fillOpacity={type === 'success' ? '0.1' : '0.2'}
            key={`area-${index}`}
            {...coordinates}
          />
        ))}
        {columnLabels.map(({ labels, ...area }, index) => (
          <ReferenceArea fill="none" key={`column-area-${index}`} {...area}>
            {labels.map(label => (
              <Label
                fill="var(--text-3-color)"
                fillOpacity="0.5"
                fontSize="6px"
                key={label.text}
                offset={label.position === 'top' ? 4 : label.position === 'topSubtitle' ? 14 : 6}
                position={label.position === 'bottom' ? 'insideBottom' : 'insideTop'}
                value={label.text}
              />
            ))}
          </ReferenceArea>
        ))}
      </RechartsScatterChart>
    </ResponsiveContainer>
  );
};

export const ScatterChart = memo(Component);
